import React, { useMemo } from 'react';

import { SearchField } from 'components/common/SearchField/SearchField';

export interface ISuggestion {
  kind: string;
  value: string;
}

const MAX_PER_KIND = 4;

const buildSuggestions = (
  sources: ISuggestion[],
  kinds: string[],
  query: string,
): ISuggestion[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const seen = new Set<string>();
  const byKind: Record<string, ISuggestion[]> = Object.fromEntries(
    kinds.map((kind) => [kind, []]),
  );

  sources.forEach(({ kind, value }) => {
    if (!value) return;
    const key = `${kind}:${value.toLowerCase()}`;
    if (seen.has(key)) return;
    if (!value.toLowerCase().includes(needle)) return;
    if (byKind[kind].length >= MAX_PER_KIND) return;
    seen.add(key);
    byKind[kind].push({ kind, value });
  });

  return kinds.flatMap((kind) => byKind[kind]);
};

interface ISuggestionSearchProps {
  sources: ISuggestion[];
  kinds: string[];
  placeholder: string;
  value: string;
  fullWidth?: boolean;
  onChange: (value: string) => void;
}

export const SuggestionSearch: React.FC<ISuggestionSearchProps> = ({
  sources,
  kinds,
  placeholder,
  value,
  fullWidth,
  onChange,
}) => {
  const options = useMemo(
    () => buildSuggestions(sources, kinds, value),
    [sources, kinds, value],
  );

  return (
    <SearchField
      value={value}
      placeholder={placeholder}
      fullWidth={fullWidth}
      suggestions={options}
      onChange={onChange}
    />
  );
};
